/**
 * Email Campaign Create/Edit Form
 * Full-featured campaign builder replicating Sendy functionality
 *
 * Created: January 3, 2026
 */

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Container, Row, Col, Card, Badge, ProgressBar } from "react-bootstrap";
import { toast } from "react-toastify";

import { selectUser } from "../../../components/auth/auth.selector";
import HelmetWrapper from "../../../HelmetWrapper";
import ReactVortoxSpinner from "../../../components/common-components/ReactVortoxSpinner";
import TestEmailModal from "../../../components/email-broadcast/TestEmailModal";

import {
  fetchEmailListsData,
  fetchEmailTemplatesData,
  fetchEmailCampaignByIdData,
  clearSelectedCampaign,
} from "../../../redux/email-broadcast/emailBroadcastSlice";
import {
  createEmailCampaign,
  updateEmailCampaign,
  scheduleCampaign,
  sendCampaignNow,
  previewEmail,
  sendTestEmail,
} from "../../../redux/email-broadcast/emailBroadcastApi";

// Step indicator component
const StepIndicator = ({ currentStep, steps }) => (
  <div className="d-flex justify-content-between mb-4">
    {steps.map((step, index) => (
      <div
        key={step.key}
        className={`text-center flex-grow-1 ${index < steps.length - 1 ? "position-relative" : ""}`}
      >
        <div
          className={`rounded-circle d-inline-flex align-items-center justify-content-center ${
            currentStep >= index ? "bg-danger text-white" : "bg-light text-muted"
          }`}
          style={{ width: "40px", height: "40px" }}
        >
          {currentStep > index ? "✓" : index + 1}
        </div>
        <div className={`mt-2 small ${currentStep >= index ? "fw-medium" : "text-muted"}`}>
          {step.label}
        </div>
        {index < steps.length - 1 && (
          <div
            className="position-absolute"
            style={{
              top: "20px",
              left: "60%",
              width: "80%",
              height: "2px",
              backgroundColor: currentStep > index ? "#D2000B" : "#dee2e6",
            }}
          />
        )}
      </div>
    ))}
  </div>
);

// Variable Insert Dropdown
const VariableDropdown = ({ onInsert }) => {
  const [isOpen, setIsOpen] = useState(false);
  const variables = [
    { key: "[Name]", description: "Subscriber name" },
    { key: "[Name,fallback=Hey]", description: "Name with fallback" },
    { key: "[Email]", description: "Subscriber email" },
    { key: "[Phone]", description: "Subscriber phone" },
    { key: "[Gym_Name]", description: "Gym name" },
  ];

  return (
    <div className="dropdown d-inline-block">
      <button
        className="btn btn-outline-secondary btn-sm dropdown-toggle"
        onClick={() => setIsOpen(!isOpen)}
        type="button"
      >
        Insert Variable
      </button>
      {isOpen && (
        <div className="dropdown-menu show">
          {variables.map((v) => (
            <button
              key={v.key}
              className="dropdown-item"
              onClick={() => {
                onInsert(v.key);
                setIsOpen(false);
              }}
            >
              <code className="text-primary">{v.key}</code>
              <small className="text-muted d-block">{v.description}</small>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const EmailCampaignForm = () => {
  const { id } = useParams();
  const isEdit = id && id !== "new";
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userInfoData = useSelector(selectUser);
  const { emailLists, emailTemplates, selectedCampaign, selectedCampaignLoading } = useSelector(
    (state) => state.emailBroadcast || {}
  );

  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showTestEmailModal, setShowTestEmailModal] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [showPreview, setShowPreview] = useState(false);

  const steps = [
    { key: "setup", label: "Setup" },
    { key: "content", label: "Content" },
    { key: "schedule", label: "Schedule" },
    { key: "review", label: "Review" },
  ];

  const [formData, setFormData] = useState({
    // Step 1: Setup
    campaign_name: "",
    list_id: "",
    from_name: "WTF Gyms",
    from_email: "noreply@wtfgyms.com",
    reply_to: "support@wtfgyms.com",

    // Step 2: Content
    subject: "",
    preheader: "",
    template_id: "",
    html_content: "",
    plain_text: "",
    use_template: false,

    // Step 3: Schedule
    schedule_type: "now", // now, later
    scheduled_date: "",
    scheduled_time: "",
    timezone: "Asia/Kolkata",

    // Tracking options
    track_opens: true,
    track_clicks: true,
    add_google_analytics: false,
    ga_source: "",
    ga_medium: "email",
    ga_campaign: "",
  });

  useEffect(() => {
    dispatch(fetchEmailListsData({ filters: {}, token: userInfoData?.token }));
    dispatch(fetchEmailTemplatesData({ filters: {}, token: userInfoData?.token }));

    if (isEdit) {
      dispatch(fetchEmailCampaignByIdData({ id, token: userInfoData?.token }));
    }

    return () => {
      dispatch(clearSelectedCampaign());
    };
  }, [dispatch, id, userInfoData?.token]);

  useEffect(() => {
    if (isEdit && selectedCampaign) {
      setFormData({
        campaign_name: selectedCampaign.campaign_name || "",
        list_id: selectedCampaign.list_id || "",
        from_name: selectedCampaign.from_name || "WTF Gyms",
        from_email: selectedCampaign.from_email || "noreply@wtfgyms.com",
        reply_to: selectedCampaign.reply_to || "support@wtfgyms.com",
        subject: selectedCampaign.subject || "",
        preheader: selectedCampaign.preheader || "",
        template_id: selectedCampaign.template_id || "",
        html_content: selectedCampaign.html_content || "",
        plain_text: selectedCampaign.plain_text || "",
        use_template: !!selectedCampaign.template_id,
        schedule_type: selectedCampaign.scheduled_at ? "later" : "now",
        scheduled_date: selectedCampaign.scheduled_at
          ? new Date(selectedCampaign.scheduled_at).toISOString().split("T")[0]
          : "",
        scheduled_time: selectedCampaign.scheduled_at
          ? new Date(selectedCampaign.scheduled_at).toTimeString().slice(0, 5)
          : "",
        timezone: selectedCampaign.timezone || "Asia/Kolkata",
        track_opens: selectedCampaign.track_opens !== false,
        track_clicks: selectedCampaign.track_clicks !== false,
        add_google_analytics: !!selectedCampaign.ga_campaign,
        ga_source: selectedCampaign.ga_source || "",
        ga_medium: selectedCampaign.ga_medium || "email",
        ga_campaign: selectedCampaign.ga_campaign || "",
      });
    }
  }, [selectedCampaign]);

  const validateStep = (step) => {
    switch (step) {
      case 0: // Setup
        if (!formData.campaign_name.trim()) {
          toast.error("Campaign name is required");
          return false;
        }
        if (!formData.list_id) {
          toast.error("Please select a subscriber list");
          return false;
        }
        return true;

      case 1: // Content
        if (!formData.subject.trim()) {
          toast.error("Subject line is required");
          return false;
        }
        if (formData.use_template && !formData.template_id) {
          toast.error("Please select a template");
          return false;
        }
        if (!formData.use_template && !formData.html_content.trim()) {
          toast.error("Email content is required");
          return false;
        }
        return true;

      case 2: // Schedule
        if (formData.schedule_type === "later") {
          if (!formData.scheduled_date || !formData.scheduled_time) {
            toast.error("Please select a date and time for scheduling");
            return false;
          }
          const scheduledDateTime = new Date(`${formData.scheduled_date}T${formData.scheduled_time}`);
          if (scheduledDateTime <= new Date()) {
            toast.error("Scheduled time must be in the future");
            return false;
          }
        }
        return true;

      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, steps.length - 1));
    }
  };

  const handlePrev = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  const handleTemplateSelect = (templateId) => {
    const template = emailTemplates?.data?.find((t) => t.uid === templateId || t.id === parseInt(templateId));
    if (template) {
      setFormData({
        ...formData,
        template_id: templateId,
        subject: template.subject || formData.subject,
        html_content: template.html_content || "",
        plain_text: template.plain_text || "",
        from_name: template.from_name || formData.from_name,
        from_email: template.from_email || formData.from_email,
        reply_to: template.reply_to || formData.reply_to,
      });
    }
  };

  const handlePreview = async () => {
    try {
      const htmlToPreview = formData.use_template
        ? emailTemplates?.data?.find((t) => t.uid === formData.template_id)?.html_content
        : formData.html_content;

      const response = await previewEmail(
        { html_content: htmlToPreview, subject: formData.subject },
        userInfoData?.token
      );
      if (response?.data?.html) {
        setPreviewHtml(response.data.html);
      } else {
        setPreviewHtml(htmlToPreview);
      }
      setShowPreview(true);
    } catch (error) {
      setPreviewHtml(formData.html_content);
      setShowPreview(true);
    }
  };

  const handleOpenTestEmailModal = () => {
    setShowTestEmailModal(true);
  };

  const handleSaveDraft = async () => {
    if (!formData.campaign_name.trim()) {
      toast.error("Campaign name is required");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        campaign_status: "draft",
      };

      let response;
      if (isEdit) {
        response = await updateEmailCampaign(id, payload, userInfoData?.token);
      } else {
        response = await createEmailCampaign(payload, userInfoData?.token);
      }

      if (response?.status) {
        toast.success("Draft saved");
        navigate("/broadcast-system/email-broadcast/campaigns");
      } else {
        toast.error(response?.message || "Save failed");
      }
    } catch (error) {
      toast.error("Save failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSendOrSchedule = async () => {
    setLoading(true);
    try {
      // First save/create the campaign
      const payload = {
        ...formData,
        campaign_status: formData.schedule_type === "later" ? "scheduled" : "sending",
      };

      let response;
      let campaignId = id;

      if (isEdit) {
        response = await updateEmailCampaign(id, payload, userInfoData?.token);
      } else {
        response = await createEmailCampaign(payload, userInfoData?.token);
        campaignId = response?.data?.uid;
      }

      if (!response?.status) {
        toast.error(response?.message || "Failed to save campaign");
        setLoading(false);
        return;
      }

      // If scheduling for later
      if (formData.schedule_type === "later") {
        const scheduleResponse = await scheduleCampaign(
          campaignId,
          {
            scheduled_at: `${formData.scheduled_date}T${formData.scheduled_time}`,
            timezone: formData.timezone,
          },
          userInfoData?.token
        );

        if (scheduleResponse?.status) {
          toast.success("Campaign scheduled successfully");
          navigate("/broadcast-system/email-broadcast/campaigns");
        } else {
          toast.error(scheduleResponse?.message || "Failed to schedule");
        }
      } else {
        // Send now
        const sendResponse = await sendCampaignNow(campaignId, userInfoData?.token);
        if (sendResponse?.status) {
          toast.success("Campaign is being sent!");
          navigate("/broadcast-system/email-broadcast/campaigns");
        } else {
          toast.error(sendResponse?.message || "Failed to send");
        }
      }
    } catch (error) {
      toast.error(error.message || "Operation failed");
    } finally {
      setLoading(false);
    }
  };

  const selectedList = emailLists?.data?.find(
    (l) => l.uid === formData.list_id || l.id === parseInt(formData.list_id)
  );

  if (isEdit && selectedCampaignLoading) {
    return (
      <HelmetWrapper title="WTF: Loading Campaign">
        <Container className="bg-cloud-k px-50px gobal-container" fluid>
          <div className="text-center py-5">
            <ReactVortoxSpinner />
          </div>
        </Container>
      </HelmetWrapper>
    );
  }

  return (
    <HelmetWrapper title={`WTF: ${isEdit ? "Edit" : "Create"} Campaign`}>
      <Container className="bg-cloud-k px-50px gobal-container" fluid>
        {/* Header */}
        <Row className="mb-4">
          <Col>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <button
                  className="btn btn-link text-muted p-0 mb-2"
                  onClick={() => navigate("/broadcast-system/email-broadcast/campaigns")}
                >
                  ← Back to Campaigns
                </button>
                <h4 className="mb-0">{isEdit ? "Edit Campaign" : "Create New Campaign"}</h4>
              </div>
              <div className="d-flex gap-2">
                <button className="btn btn-outline-secondary" onClick={handleSaveDraft} disabled={loading}>
                  Save Draft
                </button>
              </div>
            </div>
          </Col>
        </Row>

        {/* Step Indicator */}
        <Row className="mb-4">
          <Col lg={8} className="mx-auto">
            <StepIndicator currentStep={currentStep} steps={steps} />
          </Col>
        </Row>

        {/* Step Content */}
        <Row>
          <Col lg={8} className="mx-auto">
            <Card>
              <Card.Body className="p-4">
                {/* Step 1: Setup */}
                {currentStep === 0 && (
                  <>
                    <h5 className="mb-4">Campaign Setup</h5>

                    <div className="mb-3">
                      <label className="form-label">Campaign Name *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.campaign_name}
                        onChange={(e) => setFormData({ ...formData, campaign_name: e.target.value })}
                        placeholder="January Newsletter"
                      />
                      <small className="text-muted">Internal name - not shown to subscribers</small>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Subscriber List *</label>
                      <select
                        className="form-select"
                        value={formData.list_id}
                        onChange={(e) => setFormData({ ...formData, list_id: e.target.value })}
                      >
                        <option value="">Select a list...</option>
                        {emailLists?.data?.map((list) => (
                          <option key={list.uid || list.id} value={list.uid || list.id}>
                            {list.name} ({list.active_count?.toLocaleString() || 0} active subscribers)
                          </option>
                        ))}
                      </select>
                    </div>

                    <hr className="my-4" />

                    <h6 className="mb-3">Sender Information</h6>

                    <Row>
                      <Col md={6}>
                        <div className="mb-3">
                          <label className="form-label">From Name</label>
                          <input
                            type="text"
                            className="form-control"
                            value={formData.from_name}
                            onChange={(e) => setFormData({ ...formData, from_name: e.target.value })}
                            placeholder="WTF Gyms"
                          />
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-3">
                          <label className="form-label">From Email</label>
                          <input
                            type="email"
                            className="form-control"
                            value={formData.from_email}
                            onChange={(e) => setFormData({ ...formData, from_email: e.target.value })}
                            placeholder="noreply@wtfgyms.com"
                          />
                        </div>
                      </Col>
                    </Row>

                    <div className="mb-3">
                      <label className="form-label">Reply-To Email</label>
                      <input
                        type="email"
                        className="form-control"
                        value={formData.reply_to}
                        onChange={(e) => setFormData({ ...formData, reply_to: e.target.value })}
                        placeholder="support@wtfgyms.com"
                      />
                    </div>
                  </>
                )}

                {/* Step 2: Content */}
                {currentStep === 1 && (
                  <>
                    <h5 className="mb-4">Email Content</h5>

                    <div className="mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <label className="form-label mb-0">Subject Line *</label>
                        <VariableDropdown
                          onInsert={(v) => setFormData({ ...formData, subject: formData.subject + v })}
                        />
                      </div>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.subject}
                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                        placeholder="Hi [Name], Check out our latest offers!"
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Preheader Text</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.preheader}
                        onChange={(e) => setFormData({ ...formData, preheader: e.target.value })}
                        placeholder="Preview text that appears after the subject line..."
                        maxLength={150}
                      />
                      <small className="text-muted">{formData.preheader.length}/150 characters</small>
                    </div>

                    <hr className="my-4" />

                    <div className="mb-3">
                      <div className="d-flex gap-3 mb-3">
                        <div className="form-check">
                          <input
                            type="radio"
                            className="form-check-input"
                            id="use-template"
                            checked={formData.use_template}
                            onChange={() => setFormData({ ...formData, use_template: true })}
                          />
                          <label className="form-check-label" htmlFor="use-template">
                            Use Template
                          </label>
                        </div>
                        <div className="form-check">
                          <input
                            type="radio"
                            className="form-check-input"
                            id="custom-html"
                            checked={!formData.use_template}
                            onChange={() => setFormData({ ...formData, use_template: false })}
                          />
                          <label className="form-check-label" htmlFor="custom-html">
                            Custom HTML
                          </label>
                        </div>
                      </div>

                      {formData.use_template ? (
                        <div className="mb-3">
                          <label className="form-label">Select Template *</label>
                          <select
                            className="form-select"
                            value={formData.template_id}
                            onChange={(e) => handleTemplateSelect(e.target.value)}
                          >
                            <option value="">Choose a template...</option>
                            {emailTemplates?.data?.map((template) => (
                              <option key={template.uid || template.id} value={template.uid || template.id}>
                                {template.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      ) : (
                        <>
                          <div className="mb-3">
                            <div className="d-flex justify-content-between mb-2">
                              <label className="form-label mb-0">HTML Content *</label>
                              <VariableDropdown
                                onInsert={(v) =>
                                  setFormData({ ...formData, html_content: formData.html_content + v })
                                }
                              />
                            </div>
                            <textarea
                              className="form-control font-monospace"
                              rows={12}
                              value={formData.html_content}
                              onChange={(e) => setFormData({ ...formData, html_content: e.target.value })}
                              style={{ fontSize: "13px" }}
                            />
                          </div>

                          <div className="mb-3">
                            <label className="form-label">Plain Text (Optional)</label>
                            <textarea
                              className="form-control"
                              rows={4}
                              value={formData.plain_text}
                              onChange={(e) => setFormData({ ...formData, plain_text: e.target.value })}
                              placeholder="Plain text version for email clients that don't support HTML..."
                            />
                          </div>
                        </>
                      )}
                    </div>

                    {/* Preview & Test */}
                    <Card className="bg-light">
                      <Card.Body>
                        <h6 className="mb-3">Preview & Test</h6>
                        <div className="d-flex gap-2 flex-wrap">
                          <button className="btn btn-outline-primary btn-sm" onClick={handlePreview}>
                            Preview Email
                          </button>
                          <div className="d-flex gap-2">
                            <button
                              className="btn btn-outline-primary btn-sm"
                              onClick={handleOpenTestEmailModal}
                            >
                              Send Test Email
                            </button>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>

                    {/* Preview Modal */}
                    {showPreview && (
                      <div className="mt-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <strong>Email Preview</strong>
                          <button className="btn-close" onClick={() => setShowPreview(false)} />
                        </div>
                        <div
                          className="border p-3 bg-white"
                          style={{ maxHeight: "400px", overflowY: "auto" }}
                          dangerouslySetInnerHTML={{ __html: previewHtml }}
                        />
                      </div>
                    )}
                  </>
                )}

                {/* Step 3: Schedule */}
                {currentStep === 2 && (
                  <>
                    <h5 className="mb-4">Schedule & Tracking</h5>

                    <div className="mb-4">
                      <label className="form-label">When to send?</label>
                      <div className="d-flex gap-3">
                        <Card
                          className={`flex-grow-1 cursor-pointer ${
                            formData.schedule_type === "now" ? "border-danger" : ""
                          }`}
                          style={{ cursor: "pointer" }}
                          onClick={() => setFormData({ ...formData, schedule_type: "now" })}
                        >
                          <Card.Body className="text-center py-4">
                            <div className="fs-2 mb-2">🚀</div>
                            <h6>Send Now</h6>
                            <small className="text-muted">Start sending immediately</small>
                          </Card.Body>
                        </Card>
                        <Card
                          className={`flex-grow-1 cursor-pointer ${
                            formData.schedule_type === "later" ? "border-danger" : ""
                          }`}
                          style={{ cursor: "pointer" }}
                          onClick={() => setFormData({ ...formData, schedule_type: "later" })}
                        >
                          <Card.Body className="text-center py-4">
                            <div className="fs-2 mb-2">📅</div>
                            <h6>Schedule for Later</h6>
                            <small className="text-muted">Choose date and time</small>
                          </Card.Body>
                        </Card>
                      </div>
                    </div>

                    {formData.schedule_type === "later" && (
                      <Row className="mb-4">
                        <Col md={4}>
                          <label className="form-label">Date *</label>
                          <input
                            type="date"
                            className="form-control"
                            value={formData.scheduled_date}
                            onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                            min={new Date().toISOString().split("T")[0]}
                          />
                        </Col>
                        <Col md={4}>
                          <label className="form-label">Time *</label>
                          <input
                            type="time"
                            className="form-control"
                            value={formData.scheduled_time}
                            onChange={(e) => setFormData({ ...formData, scheduled_time: e.target.value })}
                          />
                        </Col>
                        <Col md={4}>
                          <label className="form-label">Timezone</label>
                          <select
                            className="form-select"
                            value={formData.timezone}
                            onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                          >
                            <option value="Asia/Kolkata">IST (India)</option>
                            <option value="UTC">UTC</option>
                            <option value="America/New_York">EST (US)</option>
                            <option value="America/Los_Angeles">PST (US)</option>
                            <option value="Europe/London">GMT (UK)</option>
                          </select>
                        </Col>
                      </Row>
                    )}

                    <hr className="my-4" />

                    <h6 className="mb-3">Tracking Options</h6>

                    <div className="mb-3">
                      <div className="form-check mb-2">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="track-opens"
                          checked={formData.track_opens}
                          onChange={(e) => setFormData({ ...formData, track_opens: e.target.checked })}
                        />
                        <label className="form-check-label" htmlFor="track-opens">
                          Track email opens
                        </label>
                      </div>
                      <div className="form-check mb-2">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="track-clicks"
                          checked={formData.track_clicks}
                          onChange={(e) => setFormData({ ...formData, track_clicks: e.target.checked })}
                        />
                        <label className="form-check-label" htmlFor="track-clicks">
                          Track link clicks
                        </label>
                      </div>
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="add-ga"
                          checked={formData.add_google_analytics}
                          onChange={(e) =>
                            setFormData({ ...formData, add_google_analytics: e.target.checked })
                          }
                        />
                        <label className="form-check-label" htmlFor="add-ga">
                          Add Google Analytics tracking
                        </label>
                      </div>
                    </div>

                    {formData.add_google_analytics && (
                      <Row className="mb-3">
                        <Col md={4}>
                          <label className="form-label">utm_source</label>
                          <input
                            type="text"
                            className="form-control"
                            value={formData.ga_source}
                            onChange={(e) => setFormData({ ...formData, ga_source: e.target.value })}
                            placeholder="newsletter"
                          />
                        </Col>
                        <Col md={4}>
                          <label className="form-label">utm_medium</label>
                          <input
                            type="text"
                            className="form-control"
                            value={formData.ga_medium}
                            onChange={(e) => setFormData({ ...formData, ga_medium: e.target.value })}
                            placeholder="email"
                          />
                        </Col>
                        <Col md={4}>
                          <label className="form-label">utm_campaign</label>
                          <input
                            type="text"
                            className="form-control"
                            value={formData.ga_campaign}
                            onChange={(e) => setFormData({ ...formData, ga_campaign: e.target.value })}
                            placeholder="january_2026"
                          />
                        </Col>
                      </Row>
                    )}
                  </>
                )}

                {/* Step 4: Review */}
                {currentStep === 3 && (
                  <>
                    <h5 className="mb-4">Review & Send</h5>

                    <Card className="mb-3">
                      <Card.Header className="bg-light">
                        <strong>Campaign Summary</strong>
                      </Card.Header>
                      <Card.Body>
                        <table className="table table-borderless mb-0">
                          <tbody>
                            <tr>
                              <td className="text-muted" style={{ width: "150px" }}>Campaign Name</td>
                              <td className="fw-medium">{formData.campaign_name}</td>
                            </tr>
                            <tr>
                              <td className="text-muted">Subscriber List</td>
                              <td>
                                {selectedList?.name || "Not selected"}
                                {selectedList && (
                                  <Badge bg="info" className="ms-2">
                                    {selectedList.active_count?.toLocaleString() || 0} subscribers
                                  </Badge>
                                )}
                              </td>
                            </tr>
                            <tr>
                              <td className="text-muted">From</td>
                              <td>
                                {formData.from_name} &lt;{formData.from_email}&gt;
                              </td>
                            </tr>
                            <tr>
                              <td className="text-muted">Subject</td>
                              <td>{formData.subject}</td>
                            </tr>
                            <tr>
                              <td className="text-muted">Content</td>
                              <td>
                                {formData.use_template
                                  ? `Using template: ${emailTemplates?.data?.find((t) => t.uid === formData.template_id)?.name || "Unknown"}`
                                  : "Custom HTML"}
                              </td>
                            </tr>
                            <tr>
                              <td className="text-muted">Schedule</td>
                              <td>
                                {formData.schedule_type === "now"
                                  ? "Send immediately"
                                  : `Scheduled: ${formData.scheduled_date} at ${formData.scheduled_time} ${formData.timezone}`}
                              </td>
                            </tr>
                            <tr>
                              <td className="text-muted">Tracking</td>
                              <td>
                                {formData.track_opens && <Badge bg="success" className="me-1">Opens</Badge>}
                                {formData.track_clicks && <Badge bg="success" className="me-1">Clicks</Badge>}
                                {formData.add_google_analytics && <Badge bg="info">Google Analytics</Badge>}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </Card.Body>
                    </Card>

                    {/* Warnings */}
                    {selectedList && selectedList.active_count > 10000 && (
                      <div className="alert alert-warning">
                        <strong>Large list:</strong> You're about to send to {selectedList.active_count?.toLocaleString()} subscribers.
                        Make sure you've tested your email and reviewed all content.
                      </div>
                    )}

                    <div className="alert alert-info">
                      <strong>Tip:</strong> Send a test email before launching to verify everything looks correct.
                    </div>

                    {/* Final Test */}
                    <Card className="bg-light mb-4">
                      <Card.Body>
                        <h6 className="mb-3">Send Final Test</h6>
                        <p className="text-muted small mb-3">
                          Send a test email to verify everything looks correct before launching your campaign.
                        </p>
                        <button
                          className="btn btn-outline-primary"
                          onClick={handleOpenTestEmailModal}
                        >
                          Send Test Email
                        </button>
                      </Card.Body>
                    </Card>
                  </>
                )}

                {/* Navigation Buttons */}
                <div className="d-flex justify-content-between mt-4 pt-3 border-top">
                  <button
                    className="btn btn-outline-secondary"
                    onClick={handlePrev}
                    disabled={currentStep === 0}
                  >
                    ← Previous
                  </button>

                  <div className="d-flex gap-2">
                    {currentStep < steps.length - 1 ? (
                      <button className="btn btn-primary" onClick={handleNext}>
                        Next →
                      </button>
                    ) : (
                      <button
                        className="btn btn-success btn-lg"
                        onClick={handleSendOrSchedule}
                        disabled={loading}
                        style={{ minWidth: "200px" }}
                      >
                        {loading
                          ? "Processing..."
                          : formData.schedule_type === "now"
                          ? "🚀 Send Campaign Now"
                          : "📅 Schedule Campaign"}
                      </button>
                    )}
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Test Email Modal */}
        <TestEmailModal
          show={showTestEmailModal}
          onHide={() => setShowTestEmailModal(false)}
          token={userInfoData?.token}
          templateId={formData.use_template ? formData.template_id : null}
          subject={formData.subject}
          htmlBody={formData.use_template ? null : formData.html_content}
          fromName={formData.from_name}
          fromEmail={formData.from_email}
          userEmail={userInfoData?.email}
        />
      </Container>
    </HelmetWrapper>
  );
};

export default EmailCampaignForm;
